import {Typography, css, useTheme} from '@mui/material';
import {
  DlMuiChip,
  DlMuiPopper,
  GetStylesFunction,
  TranslateUtils,
  mixins,
  useMuiAnchor,
  asQueryStrings,
  useLocationQuery,
} from '@wallaby-ui/core';
import * as React from 'react';
import {useIntl} from 'react-intl';
import {useStore} from 'react-redux';
import {useParams} from 'react-router-dom';
import {WallabyCoreSchema} from 'src/js/common/types';
import {EntityLink} from 'src/js/containers/file/components/EntityLink';
import {TemplatedCellRendererParams} from 'src/js/containers/grid/grid_types';
import {getHospitalizationDocumentLink} from 'src/js/containers/hospitalizations/hospitalizations_helpers';
import {baseDocumentsListLinkFormatterParams} from 'src/js/lib/raw_rendering/base_documents_list_link_formatter_params';

type Props = TemplatedCellRendererParams<WallabyCoreSchema.components['schemas']['DocumentForEncounterDto']>;

export const DocumentNameFormatter: React.FC<Props> = (props) => {
  const {value, data} = props;

  const hasTooltip = !!value;
  const theme = useTheme();
  const intl = useIntl();
  const styles = getStyles({theme});
  const store = useStore();
  const routeParams = useParams();

  const {documentId, hospitalizationId, searchParams} = baseDocumentsListLinkFormatterParams(props, {
    routeParams,
    store,
  });
  const [query] = useLocationQuery(`/hospitalizedPatients/${hospitalizationId}`);
  const queryStrings = asQueryStrings(query);

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(hasTooltip);

  const isRelatedToEncounterTitle = data.isRelatedToEncounter
    ? TranslateUtils.translate('documents.list.isRelatedToEncounter.tooltip', intl)
    : undefined;

  const route = hospitalizationId && documentId ? getHospitalizationDocumentLink(hospitalizationId, documentId, '') : '';

  return (
    <div css={styles.wrapper}>
      <Typography onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave} css={styles.typography} variant={'textButton'}>
        <EntityLink
          route={route}
          searchParams={{
            ...(searchParams ?? {}),
            ...queryStrings,
            selectedTabs: JSON.stringify({
              ...query.selectedTabs,
              hospitalizations: 1,
            }),
          }}
          suppressPadding
        >
          {value}
        </EntityLink>
        {data.isRelatedToEncounter ? <DlMuiChip label={'documents.list.isRelatedToEncounter'} css={styles.chip} /> : null}
      </Typography>
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <div css={styles.popperWrapper}>
          <Typography variant={'popper'}>{value}</Typography>
          {data.isRelatedToEncounter && <Typography variant={'popper'}>{isRelatedToEncounterTitle}</Typography>}
        </div>
      </DlMuiPopper>
    </div>
  );
};
DocumentNameFormatter.displayName = 'DocumentNameFormatter';

const getStyles: GetStylesFunction = (options) => ({
  popperWrapper: css`
    display: flex;
    flex-direction: column;
  `,
  chip: css`
    margin-left: ${options?.theme?.spacing(1)};
  `,
  typography: css`
    padding: ${options?.theme?.spacing(1, 0)};
    text-decoration: underline;
  `,
  wrapper: css`
    ${mixins.dlTextOverflowEllipses};
    min-width: 0;
  `,
});
